"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseEntityListReorderAttr = parseEntityListReorderAttr;
exports.normalizeEntityListReorderCondition = normalizeEntityListReorderCondition;
exports.applyEntityListReorderFlags = applyEntityListReorderFlags;
exports.attachEntityListReorderFlags = attachEntityListReorderFlags;
exports.findEntityListReorderTarget = findEntityListReorderTarget;
exports.swapAdjacentEntityPositions = swapAdjacentEntityPositions;
const constants_1 = require("./constants");
const computeEntityListRows_1 = require("./computeEntityListRows");
const colorPickerRichText_1 = require("../utils/colorPickerRichText");
/** Parse Entity-List `reorder=` (`true` / `yes` / `1`). */
function parseEntityListReorderAttr(raw) {
    const v = String(raw ?? '')
        .trim()
        .toLowerCase();
    return v === 'true' || v === 'yes' || v === '1';
}
/**
 * Normalize `reorderCondition` so a bare function name becomes a call.
 * `"isSetListOwnerEditor()"` and `"isSetListOwnerEditor"` both work.
 */
function normalizeEntityListReorderCondition(raw) {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
        return '';
    }
    if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmed)) {
        return `${trimmed}()`;
    }
    return trimmed;
}
function applyEntityListReorderFlags(rows) {
    if (rows.length < 2) {
        return rows;
    }
    return rows.map((row, index) => ({
        ...row,
        canMoveUp: index > 0,
        canMoveDown: index < rows.length - 1
    }));
}
/**
 * Attach per-row move flags when this embed opted into reorder and the actor
 * passes the optional condition. No-op for foreign lists or non-position sorts.
 */
function attachEntityListReorderFlags(el, evaluateCondition) {
    if (!el || el.type !== 'entity-list' || !el.entityListReorder) {
        return;
    }
    if ((el.entityListSource || '').trim()) {
        return;
    }
    if ((0, computeEntityListRows_1.parseEntityListSortAttr)(el.entityListSort) !== 'position') {
        return;
    }
    const condition = normalizeEntityListReorderCondition(el.entityListReorderCondition);
    if (condition && !evaluateCondition(condition)) {
        return;
    }
    const rows = el.entityListRows;
    if (!Array.isArray(rows) || rows.length < 2) {
        return;
    }
    el.entityListRows = applyEntityListReorderFlags(rows);
}
function walkEntityListReorderTarget(elements, entityId, direction) {
    for (const el of elements) {
        if (el.type === 'entity-list' && el.entityListReorder) {
            const rows = el.entityListRows ?? [];
            const index = rows.findIndex(row => row.entityId === entityId);
            if (index >= 0) {
                const row = rows[index];
                const allowed = direction === 'up' ? row.canMoveUp === true : row.canMoveDown === true;
                if (allowed) {
                    return { orderedIds: rows.map(r => r.entityId) };
                }
            }
        }
        const nested = [];
        if (el.type === 'conditional') {
            if (Array.isArray(el.children)) {
                nested.push(...el.children);
            }
            if (Array.isArray(el.elseChildren)) {
                nested.push(...el.elseChildren);
            }
        }
        else if (Array.isArray(el.content)) {
            for (const item of el.content) {
                if (item && typeof item === 'object' && 'type' in item) {
                    nested.push(item);
                }
            }
        }
        if (nested.length > 0) {
            const hit = walkEntityListReorderTarget(nested, entityId, direction);
            if (hit) {
                return hit;
            }
        }
    }
    return null;
}
/** Find a reorder-enabled Entity-List on the hydrated scene that can move this row. */
function findEntityListReorderTarget(scene, entityId, direction) {
    for (const fragment of (0, colorPickerRichText_1.collectRichTextSourcesFromScene)(scene)) {
        const desc = (0, colorPickerRichText_1.toRichTextDescription)(fragment);
        if (!desc) {
            continue;
        }
        const hit = walkEntityListReorderTarget(desc.elements, entityId, direction);
        if (hit) {
            return hit;
        }
    }
    return null;
}
/**
 * Swap `position` with the adjacent sibling in `orderedIds` and densify 0..n-1.
 * Returns false when the move is out of range.
 */
function swapAdjacentEntityPositions(entitiesService, orderedIds, entityId, direction) {
    const index = orderedIds.indexOf(entityId);
    if (index < 0) {
        return false;
    }
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= orderedIds.length) {
        return false;
    }
    const reordered = [...orderedIds];
    const current = reordered[index];
    reordered[index] = reordered[swapIndex];
    reordered[swapIndex] = current;
    for (let i = 0; i < reordered.length; i++) {
        const id = reordered[i];
        if (!entitiesService.setEntityVariable(id, constants_1.ENTITY_POSITION_VAR, String(i), 'string')) {
            return false;
        }
    }
    return true;
}
//# sourceMappingURL=entityListReorder.js.map